import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import "./ManagerLogin.css";

const ManagerLogin = () => {
  const [email, setEmail] = useState("");
  const [managerId, setManagerId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!email || !managerId) {
      setError("Please enter both email and Manager ID");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/managers/request-otp`, {
        email,
        manager_id: managerId
      });

      if (response.status === 200) {
        setOtpSent(true);
        setSuccess(`Verification code sent to ${email}`);
        setError("");
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      const errorMessage = error.response?.data?.message || 
                          "Failed to send OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/managers/verify-otp`, {
        email,
        manager_id: managerId,
        otp
      });

      if (response.status === 200) {
        setSuccess("Login successful!");
        setError("");
        
        const token = response.data.token;
        const managerData = response.data.manager;
        
        sessionStorage.setItem("managerToken", token);
        sessionStorage.setItem("managerData", JSON.stringify(managerData));
        sessionStorage.setItem("managerEmail", email);
        sessionStorage.setItem("managerId", managerId);
        sessionStorage.setItem("managerName", managerData.name || "");
        sessionStorage.setItem("companyName", managerData.company_name || "");

        navigate("/managerdashboard");
      } else {
        setError(response.data.message || "Verification failed");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const errorMessage = error.response?.data?.message || 
                          "Verification failed. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp("");
    clearMessages();
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    clearMessages();
  };

  return (
    <div className="mgr-auth-wrapper">
      {/* Background */}
      <div className="mgr-bg-layer">
        <div className="mgr-bg-gradient"></div>
      </div>

      {/* Main Content Grid */}
      <div className="mgr-content-grid">
        {/* Left Panel - Branding & Features */}
        
        {/* Right Panel - Auth Form */}
        <div className="mgr-form-panel">
          <div className="mgr-form-container">
             
            {!otpSent ? (
              // Step 1: Credentials Entry
              <div className="mgr-form-card">
                <div className="hrcp-logo-container">
                <div className="quantum-nav__logo">
                  <div className="quantum-nav__logo-orb">
                    <img 
                      src="/images/logo415.png" 
                      alt="Logo" 
                      className="quantum-nav__logo-img" 
                    />
                    <span className="Humanex_logo_title">
                      umanex
                    </span>
                  </div>
                </div>
              </div>
                <div className="mgr-form-header">
                  <h2 className="mgr-form-title">Welcome Back</h2>
                  <p className="mgr-form-subtitle">Sign in to access your management dashboard</p>
                </div>

                {error && (
                  <div className="mgr-alert mgr-alert-error">
                    <span className="mgr-alert-icon">⚠️</span>
                    <span className="mgr-alert-text">{error}</span>
                  </div>
                )}

                {success && (
                  <div className="mgr-alert mgr-alert-success">
                    <span className="mgr-alert-icon">✓</span>
                    <span className="mgr-alert-text">{success}</span>
                  </div>
                )}

                <form className="mgr-auth-form" onSubmit={handleSendOTP}>
                  <div className="mgr-input-group">
                    <label className="mgr-input-label">
                      <span className="mgr-label-text">Manager ID</span>
                      <span className="mgr-label-required">*</span>
                    </label>
                    <div className="mgr-input-wrapper">
                      <span className="mgr-input-icon"></span>
                      <input
                        type="text"
                        className="mgr-input-field"
                        value={managerId}
                        onChange={(e) => {
                          setManagerId(e.target.value);
                          clearMessages();
                        }}
                        placeholder="Enter your Manager ID"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="mgr-input-group">
                    <label className="mgr-input-label">
                      <span className="mgr-label-text">Work Email</span>
                      <span className="mgr-label-required">*</span>
                    </label>
                    <div className="mgr-input-wrapper">
                      <span className="mgr-input-icon"></span>
                      <input
                        type="email"
                        className="mgr-input-field"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          clearMessages();
                        }}
                        placeholder="name@company.com"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="mgr-btn mgr-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="mgr-spinner"></span>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Verification Code</span>
                        <span className="mgr-btn-arrow">→</span>
                      </>
                    )}
                  </button>

                  <div className="mgr-security-note">
                    <span className="mgr-security-icon">🔒</span>
                    <span className="mgr-security-text">Secure OTP authentication for authorized managers only</span>
                  </div>
                </form>
              </div>
            ) : (
              // Step 2: OTP Verification
              <div className="mgr-form-card">
                <div className="mgr-form-header">
                  <button 
                    className="mgr-back-btn"
                    onClick={handleBack}
                    type="button"
                  >
                    ← Back
                  </button>
                  <h2 className="mgr-form-title">Verify Your Email</h2>
                  <p className="mgr-form-subtitle">
                    We've sent a 6-digit code to<br/>
                    <strong className="mgr-email-highlight">{email}</strong>
                  </p>
                </div>

                {error && (
                  <div className="mgr-alert mgr-alert-error">
                    <span className="mgr-alert-icon">⚠️</span>
                    <span className="mgr-alert-text">{error}</span>
                  </div>
                )}

                {success && (
                  <div className="mgr-alert mgr-alert-success">
                    <span className="mgr-alert-icon">✓</span>
                    <span className="mgr-alert-text">{success}</span>
                  </div>
                )}

                <form className="mgr-auth-form" onSubmit={handleVerifyOTP}>
                  <div className="mgr-input-group">
                    <label className="mgr-input-label">
                      <span className="mgr-label-text">Verification Code</span>
                    </label>
                    <input
                      type="text"
                      className="mgr-input-field mgr-otp-input"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="000000"
                      maxLength={6}
                      required
                      disabled={isSubmitting}
                    />
                    <p className="mgr-input-hint">Enter the 6-digit code from your email</p>
                  </div>

                  <button 
                    type="submit" 
                    className="mgr-btn mgr-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="mgr-spinner"></span>
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <span>Verify & Access Dashboard</span>
                        <span className="mgr-btn-arrow">→</span>
                      </>
                    )}
                  </button>

                  <button 
                    type="button"
                    className="mgr-btn mgr-btn-secondary"
                    onClick={handleSendOTP}
                    disabled={isSubmitting}
                  >
                    Resend Code
                  </button>
                </form>
              </div>
            )}

            {/* Footer */}
             {/* <div className="mgr-form-footer">            
                <p className="mgr-footer-text">
                <span className="mgr-footer-icon">🔐</span>
                Protected by enterprise-grade encryption 
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerLogin;